import React from 'react';
import {
	Link,
	TextLabel,
	Heading,
	TextBlock,
	ExtendedTheme,
	useTheme
} from 'neon-design-system';
import { createStyles, makeStyles, useMediaQuery } from '@material-ui/core';
import { getChannelPublisherUrl } from '../../../utils/channel.utils';
import { ChannelSubscriptionButton } from './ChannelSubscriptionButton';
import { Channel, ChannelTypes } from '../../../shared/types';
import { DEFAULT_CHANNEL_COVER } from '../../../config';

const useStyles = makeStyles<ExtendedTheme, StyleProps>((theme: ExtendedTheme) =>
	createStyles({
		root: {
			backgroundColor: theme.themeColors.blueGrey[800],
			borderRadius: theme.spacing(1),
			overflow: 'hidden'
		},
		cardTop: {
			position: 'relative',
			display: 'flex',
			alignItems: 'flex-end',
			backgroundImage: (props: StyleProps) => props.backgroundImageUrl && `url('${props.backgroundImageUrl}')`,
			height: ({ compact }) => compact ? 124 : 250,
			backgroundSize: 'cover'
		},
		textLabelWrapper: {
			zIndex: 2,
			position: 'absolute',
			top: ({ compact }) => compact ? theme.spacing(2) : theme.spacing(4),
			left: ({ compact }) => compact ? theme.spacing(2) : theme.spacing(4)
		},
		topDetailsWrapper: {
			zIndex: 1,
			display: 'flex',
			width: '100%',
			alignItems: ({ compact }) => compact ? 'flex-end' : 'center',
			height: ({ compact }) => compact ? 124 : 96,
			gap: theme.spacing(4),
			backgroundColor: theme.themeColors.blueGrey[800] + '99', // 99 is 60% opacity added on the HEX color
			padding: ({ compact }) => theme.spacing(1, compact ? 2 : 4)
		},
		topDetails: {
			display: 'flex',
			flexDirection: 'column',
			flex: 1,
			gap: theme.spacing(0.5)
		},
		channelNameHeading: {
			color: theme.themeColors.common.white
		},
		channelName: {
			color: theme.themeColors.common.white,
			...theme.customTypography.title2.medium,
			fontWeight: 'bold',

			[theme.breakpoints.up('sm')]: {
				...theme.customTypography.largeTitle.medium
			},

			'&:hover': {
				color: theme.themeColors.blueGrey[100]
			}
		},
		publishedBy: {
			'& a': { fontSize: 'inherit' }
		},
		publisherLink: {},
		cardBottom: {
			display: 'flex',
			flexDirection: ({ compact }) => compact ? 'column' : undefined,
			justifyContent: 'space-between',
			padding: ({ compact }) => theme.spacing(compact ? 2 : 4),
			gap: theme.spacing(2)
		},
		moreLink: {
			marginTop: theme.spacing(1),
			'& a': { fontSize: 'inherit' }
		},
		profilePic: {
			width: 80,
			height: 80,
			borderRadius: '100%',
			border: '3px solid white',
			backgroundSize: 'cover',
			backgroundPosition: 'center'
		},
		description: {
			color: theme.themeColors.blueGrey[50],
			maxWidth: 800
		}
	})
);

interface StyleProps {
	backgroundImageUrl: string;
	compact: boolean;
}

interface ChannelCoverProps {
	channel: Channel;
	disclaimerText?: JSX.Element | string;
}

const ChannelCover = ({
	channel,
	disclaimerText
}: ChannelCoverProps) => {
	const { theme } = useTheme();
	const xs = useMediaQuery(theme.breakpoints.down('xs'));
	const compact = xs;
	const classes = useStyles({ backgroundImageUrl: channel?.coverImage || DEFAULT_CHANNEL_COVER, compact });

	if (!channel) return <></>;

	return (
		<>
			<div className={classes.root} id="channel-cover">
				<div className={classes.cardTop}>
					{
						channel.type === ChannelTypes.premium && <div className={classes.textLabelWrapper}>
							<TextLabel label="Premium" styleName="primary" />
						</div>
					}
					<div className={classes.topDetailsWrapper}>
						{
							!compact && channel.profileImage && (
								<div className={classes.profilePic} style={{backgroundImage: `url(${channel.profileImage})`}} />
							)
						}
						<div className={classes.topDetails}>
							<Heading className={classes.channelNameHeading} styleName={compact ? 'title2' : 'largeTitle'}>{channel.name}</Heading>
							<TextBlock styleName={compact ? 'caption1' : 'body'} className={classes.publishedBy}>
								Published by{' '}
								<Link to={getChannelPublisherUrl(channel)}><strong>{channel.owner.name}</strong></Link>
							</TextBlock>
						</div>
					</div>
				</div>
				<div className={classes.cardBottom}>
					<div>
						<TextBlock styleName={compact ? 'body' : 'largeBody2'} className={classes.description}>
							{channel.description}
						</TextBlock>
						{
							channel.url && !compact && (
								<TextBlock styleName="caption2" className={classes.moreLink}>
									<Link to={channel.url} ignoreRouter target="_blank"><strong>Learn more</strong></Link>
								</TextBlock>
							)
						}
					</div>
					<ChannelSubscriptionButton
						disclaimer={!compact}
						disclaimerText={disclaimerText}
						channel={channel}
						size={compact ? 'small' : 'large'}
						align={compact ? 'left' : 'center'}
					/>
				</div>
			</div>
		</>
	);
};

export default ChannelCover;
